import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { formatCurrency, getCurrencySymbol } from '../../lib/utils';
import { useApp } from '../../context/AppContext';

// Color palette for measures
const COLORS = [
  '#0d9488', '#0891b2', '#0284c7', '#2563eb', '#4f46e5',
  '#7c3aed', '#9333ea', '#c026d3', '#db2777', '#e11d48',
  '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a',
];

// Create readable short names for measures
const getShortName = (fullName) => {
  if (!fullName) return '';
  
  // Common abbreviations mapping - use very short names for chart labels
  const abbreviations = {
    'Training Scope Index': 'Training Scope',
    'SME Training Scope Index': 'SME Training',
    'Community Learning Index': 'Community Learning',
    'Voluntary Learning Engagement Index': 'Voluntary Learning',
    'Learning Technology Utilization Index': 'Learning Tech',
    'Learning Technology Adoption Index': 'Learning Tech',
    'Organizational Learning Culture Index': 'Learning Culture',
    'Training Performance Index (TPI)': 'Training Perf (TPI)',
    'Training Performance Index': 'Training Perf',
    'Net Promoter Score (NPS)': 'NPS',
    'Net Promoter Score for Learning': 'NPS Learning',
    'Skills Assessment Performance Index (SAPI)': 'Skills Assess (SAPI)',
    'Skills Assessment Performance Index': 'Skills Assessment',
    'Skills Application & Performance Index': 'Skills Application',
    'Skills Coverage Index': 'Skills Coverage',
    'Certification Depth Index': 'Certification',
    'Readiness Index': 'Readiness',
    'Cost Efficiency Index': 'Cost Efficiency',
    'Scheduling Velocity Index': 'Scheduling',
    'KPI Alignment Strength Index': 'KPI Alignment',
    'KPI Alignment Index': 'KPI Alignment',
  };
  
  // Check for exact match first
  if (abbreviations[fullName]) {
    return abbreviations[fullName];
  }
  
  // Try partial match for variations
  for (const [key, value] of Object.entries(abbreviations)) {
    if (fullName.includes(key.replace(' Index', '')) || key.includes(fullName.replace(' Index', ''))) {
      return value;
    }
  }
  
  // Fallback: truncate long names intelligently
  if (fullName.length > 20) {
    // Remove common suffixes
    let short = fullName
      .replace(' Index', '')
      .replace(' (TPI)', '')
      .replace(' (NPS)', '')
      .replace(' (SAPI)', '');
    
    // If still too long, take first 18 chars
    if (short.length > 18) {
      return short.substring(0, 18) + '...';
    }
    return short;
  }
  
  return fullName;
};

const CustomTooltip = ({ active, payload, valueType, currency }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 max-w-xs">
        <p className="font-medium text-slate-900 text-sm">{data.fullName}</p>
        <p className="text-xs text-slate-500">{data.pillar}</p>
        <div className="mt-2 space-y-1">
          {valueType === 'kpi' && (
            <p className="text-sm">
              KPI Contribution: <span className="font-semibold text-teal-600">{data.value?.toFixed(3)}%</span>
            </p>
          )}
          {valueType === 'financial' && (
            <p className="text-sm">
              Financial Impact: <span className="font-semibold text-emerald-600">{formatCurrency(data.value, currency)}</span>
            </p>
          )}
          <p className="text-sm">
            Share of Total: <span className="font-semibold">{data.percentage?.toFixed(1)}%</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 max-w-xs">
        <p className="font-medium text-slate-900 text-sm">{data.fullName}</p>
        <p className="text-sm mt-1">
          Share: <span className="font-semibold">{data.percentage?.toFixed(1)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

export const MeasureContributionChart = ({ 
  measureResults, 
  totalKpiDelta,
  totalFinancialImpact,
  title = "Measure Contributions",
  showKpi = true,
  showFinancial = true,
}) => {
  const { currency } = useApp();
  
  if (!measureResults || measureResults.length === 0) {
    return null;
  }

  // Prepare data for KPI contribution with readable short names
  const kpiData = measureResults
    .filter(m => m.kpi_delta_percent > 0)
    .map((m, idx) => ({
      name: getShortName(m.lei_measure_name),
      fullName: m.lei_measure_name,
      pillar: m.pillar,
      value: m.kpi_delta_percent,
      percentage: totalKpiDelta > 0 ? (m.kpi_delta_percent / totalKpiDelta) * 100 : 0,
      color: COLORS[idx % COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  // Prepare data for Financial contribution with readable short names
  const financialData = measureResults
    .filter(m => m.financial_impact > 0)
    .map((m, idx) => ({
      name: getShortName(m.lei_measure_name),
      fullName: m.lei_measure_name,
      pillar: m.pillar,
      value: m.financial_impact,
      percentage: totalFinancialImpact > 0 ? (m.financial_impact / totalFinancialImpact) * 100 : 0,
      color: COLORS[idx % COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  // Calculate dynamic height based on number of items - more spacing
  const chartHeight = Math.max(350, kpiData.length * 38);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* KPI Contribution Bar Chart */}
      {showKpi && kpiData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">KPI Impact by Measure</CardTitle>
            <CardDescription className="text-xs">
              How each measure contributes to total KPI uplift
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={kpiData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    tickFormatter={(v) => `${v.toFixed(2)}%`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: '#374151' }}
                    width={130}
                    tickLine={false}
                    interval={0}
                  />
                  <Tooltip content={<CustomTooltip valueType="kpi" currency={currency} />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={22}>
                    {kpiData.map((entry, index) => (
                      <Cell key={`cell-${entry.name || index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Contribution Bar Chart */}
      {showFinancial && financialData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Financial Impact by Measure</CardTitle>
            <CardDescription className="text-xs">
              Value contribution from each measure ({getCurrencySymbol(currency)})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={financialData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    tickFormatter={(v) => `${getCurrencySymbol(currency)}${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: '#374151' }}
                    width={130}
                    tickLine={false}
                    interval={0}
                  />
                  <Tooltip content={<CustomTooltip valueType="financial" currency={currency} />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={22}>
                    {financialData.map((entry, index) => (
                      <Cell key={`cell-${entry.name || index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Pie chart variant for showing share breakdown
export const MeasureSharePieChart = ({ 
  measureResults, 
  totalFinancialImpact,
  title = "Impact Share Distribution" 
}) => {
  if (!measureResults || measureResults.length === 0) {
    return null;
  }

  // Process and sort data
  const allData = measureResults
    .filter(m => m.financial_impact > 0)
    .map((m, idx) => ({
      name: getShortName(m.lei_measure_name),
      fullName: m.lei_measure_name,
      value: m.financial_impact,
      percentage: totalFinancialImpact > 0 ? (m.financial_impact / totalFinancialImpact) * 100 : 0,
      fill: COLORS[idx % COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  // Limit to top 8 items for legend, group rest as "Other"
  const MAX_LEGEND_ITEMS = 8;
  let pieData;
  
  if (allData.length > MAX_LEGEND_ITEMS) {
    const topItems = allData.slice(0, MAX_LEGEND_ITEMS - 1);
    const otherItems = allData.slice(MAX_LEGEND_ITEMS - 1);
    const otherValue = otherItems.reduce((sum, item) => sum + item.value, 0);
    const otherPercentage = otherItems.reduce((sum, item) => sum + item.percentage, 0);
    
    pieData = [
      ...topItems,
      {
        name: `Other (${otherItems.length})`,
        fullName: `Other ${otherItems.length} measures`,
        value: otherValue,
        percentage: otherPercentage,
        fill: '#94a3b8', // Slate gray for "Other"
      }
    ];
  } else {
    pieData = allData;
  }

  // Custom label renderer - only show for larger segments
  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percentage, index }) => {
    // Only show labels for segments >= 10% to avoid overlap
    if (percentage < 10) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text
        x={x}
        y={y}
        fill={pieData[index]?.fill || '#666'}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
      >
        {`${percentage.toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading">{title}</CardTitle>
        <CardDescription className="text-xs">
          Relative contribution of each measure to total financial impact
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="35%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                label={renderCustomLabel}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${entry.name || index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend 
                layout="vertical" 
                align="right" 
                verticalAlign="middle"
                wrapperStyle={{ right: 0, maxWidth: '50%', fontSize: '11px' }}
                formatter={(value) => (
                  <span className="text-xs text-slate-600 leading-none">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default MeasureContributionChart;
